import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import AppShell from '../components/AppShell';
import { 
  Sparkles, Video, Image as ImageIcon,
  Loader2, Music, Film, Megaphone, Users, Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateStudioPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTool, setSelectedTool] = useState(null);
  const [creating, setCreating] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    prompt: '',
    platform: 'instagram',
    category: 'general',
    voice: 'nova',
    style: 'hyperrealism',
    quality_profile: 'fast'
  });

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/platforms`);
      setPlatforms(response.data.platforms || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch platforms');
    }
  };

  const creationTools = [
    {
      id: 'short_video',
      name: 'Short Video',
      description: 'Create viral Reels, TikToks, YouTube Shorts',
      icon: Video,
      gradient: 'from-purple-500 to-pink-500',
      examples: [
        'A fun video about 5 morning productivity hacks',
        'Create an energetic workout motivation clip',
        'Explain why coffee is essential in a quirky way'
      ],
      endpoint: '/api/reels/',
      navigateTo: '/reels'
    },
    {
      id: 'image',
      name: 'AI Image',
      description: 'Generate stunning visuals from text',
      icon: ImageIcon,
      gradient: 'from-blue-500 to-cyan-500',
      examples: [
        'A serene sunset over a futuristic city',
        'Professional headshot of a confident entrepreneur',
        'Vibrant food photography of avocado toast'
      ],
      endpoint: '/api/images/generate',
      navigateTo: null
    },
    {
      id: 'long_video',
      name: 'Long Video',
      description: 'Create in-depth YouTube content (1-10 min)',
      icon: Film,
      gradient: 'from-orange-500 to-red-500',
      examples: [
        'A detailed tutorial on starting a business',
        'Documentary-style exploration of AI technology',
        '10-minute guided meditation for beginners'
      ],
      endpoint: '/api/videos/long',
      navigateTo: null,
      comingSoon: true
    },
    {
      id: 'video_ad',
      name: 'Video Ad',
      description: 'Generate compelling ad creatives',
      icon: Megaphone,
      gradient: 'from-green-500 to-emerald-500',
      examples: [
        'A 15-second ad for my fitness app targeting busy professionals',
        'Product showcase for organic skincare line',
        'Promo video for online course launch'
      ],
      endpoint: '/api/ads/generate',
      navigateTo: null,
      comingSoon: true
    },
    {
      id: 'music',
      name: 'AI Music',
      description: 'Generate royalty-free background tracks',
      icon: Music,
      gradient: 'from-indigo-500 to-purple-500',
      examples: [
        'Upbeat electronic track for workout videos',
        'Calm ambient music for meditation content',
        'Dramatic cinematic score for product reveal'
      ],
      endpoint: '/api/music/generate',
      navigateTo: null,
      comingSoon: true
    },
    {
      id: 'influencer',
      name: 'AI Influencer',
      description: 'Create a digital persona or AI clone',
      icon: Users,
      gradient: 'from-amber-500 to-orange-500',
      examples: [
        'A friendly tech reviewer named Alex who explains gadgets simply',
        'An energetic fitness coach called Maya who motivates beginners',
        'A calm meditation guide named Sage with a soothing presence'
      ],
      endpoint: '/api/influencers/',
      navigateTo: '/influencers'
    }
  ];

  const handleCreate = async () => {
    if (!formData.prompt.trim()) {
      toast.error('Please describe what you want to create');
      return;
    }

    if (!selectedTool) return;

    if (selectedTool.comingSoon) {
      toast.info('This feature is coming soon! Try Short Video or AI Influencer.');
      return;
    }

    setCreating(true);
    try {
      let payload = { prompt: formData.prompt };
      
      if (selectedTool.id === 'short_video') {
        payload = {
          ...payload,
          platform: formData.platform,
          category: formData.category,
          voice: formData.voice,
          quality_profile: formData.quality_profile
        };
      } else if (selectedTool.id === 'influencer') {
        payload = {
          ...payload,
          style: { style_type: formData.style }
        };
      } else if (selectedTool.id === 'image') {
        payload = {
          ...payload,
          style: formData.style
        };
      }

      const response = await axios.post(
        `${BACKEND_URL}${selectedTool.endpoint}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Delphi is creating your ${selectedTool.name.toLowerCase()}...`);
      setSelectedTool(null);
      setFormData({ prompt: '', platform: 'instagram', category: 'general', voice: 'nova', style: 'hyperrealism', quality_profile: 'fast' });

      if (selectedTool.navigateTo) {
        navigate(selectedTool.navigateTo);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to create ${selectedTool.name.toLowerCase()}`);
    } finally {
      setCreating(false);
    }
  };

  const voiceOptions = [
    { value: 'nova', label: 'Nova', desc: 'Energetic' },
    { value: 'alloy', label: 'Alloy', desc: 'Balanced' },
    { value: 'echo', label: 'Echo', desc: 'Calm' },
    { value: 'fable', label: 'Fable', desc: 'Storytelling' },
    { value: 'onyx', label: 'Onyx', desc: 'Authoritative' },
    { value: 'shimmer', label: 'Shimmer', desc: 'Cheerful' }
  ];

  const styleOptions = [
    { value: 'hyperrealism', label: 'Photorealistic' },
    { value: 'animated', label: '3D Animated' },
    { value: 'anime', label: 'Anime' },
    { value: 'artistic', label: 'Artistic' },
    { value: 'stylized', label: 'Stylized' }
  ];

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10">
          {/* Header */}
          <div className="mb-10">
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Production Engine</p>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight text-white">
              Generate New Creative
            </h1>
            <p className="text-zinc-500 text-base max-w-2xl">
              Describe what you want in plain language. Delphi handles everything else.
            </p>
          </div>

          {/* Creation Tools Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {creationTools.map((tool) => (
              <button
                key={tool.id}
                onClick={() => setSelectedTool(tool)}
                className={`card-oracle p-6 text-left hover:border-primary/50 transition-all group relative ${
                  tool.comingSoon ? 'opacity-60' : ''
                }`}
                data-testid={`create-tool-${tool.id}`}
              >
                {tool.comingSoon && (
                  <span className="absolute top-3 right-3 text-xs bg-secondary px-2 py-1 rounded-full">
                    Coming Soon
                  </span>
                )}
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tool.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <tool.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{tool.name}</h3>
                <p className="text-muted-foreground text-sm">{tool.description}</p>
              </button>
            ))}
          </div>

          {/* Info Section */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground">
              All tools are <span className="text-primary font-medium">prompt-driven</span> — no technical knowledge required.
            </p>
          </div>
      </div>

      {/* Creation Dialog */}
      <Dialog open={!!selectedTool} onOpenChange={(open) => !open && setSelectedTool(null)}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              {selectedTool && (
                <>
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedTool.gradient} flex items-center justify-center`}>
                    {selectedTool && <selectedTool.icon className="w-5 h-5 text-white" />}
                  </div>
                  Create {selectedTool.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedTool && (
            <div className="space-y-6 mt-4">
              {/* Prompt Input */}
              <div className="space-y-2">
                <Label>Describe what you want to create *</Label>
                <Textarea
                  data-testid="create-prompt-input"
                  placeholder={`Tell Delphi what you want. Examples:\n• ${selectedTool.examples[0]}\n• ${selectedTool.examples[1]}`}
                  value={formData.prompt}
                  onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                  className="input-oracle min-h-[120px]"
                />
                <p className="text-xs text-muted-foreground">
                  Delphi will interpret your prompt and handle all the details automatically.
                </p>
              </div>

              {/* Tool-specific options */}
              {selectedTool.id === 'short_video' && (
                <>
                  <div className="space-y-2">
                    <Label>Target Platform</Label>
                    <Select value={formData.platform} onValueChange={(v) => setFormData({ ...formData, platform: v })}>
                      <SelectTrigger className="input-oracle">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {platforms.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                        <SelectTrigger className="input-oracle">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {categories.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Voice</Label>
                      <Select value={formData.voice} onValueChange={(v) => setFormData({ ...formData, voice: v })}>
                        <SelectTrigger className="input-oracle">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {voiceOptions.map(v => (
                            <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Production Quality</Label>
                    <Select value={formData.quality_profile} onValueChange={(v) => setFormData({ ...formData, quality_profile: v })}>
                      <SelectTrigger className="input-oracle" data-testid="quality-profile-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="fast" data-testid="quality-fast">Fast — quick & economical</SelectItem>
                        <SelectItem value="quality" data-testid="quality-quality">Quality — premium voice & visuals</SelectItem>
                        <SelectItem value="best" data-testid="quality-best">Best — strongest voice, visuals & music</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {(selectedTool.id === 'influencer' || selectedTool.id === 'image') && (
                <div className="space-y-2">
                  <Label>Visual Style</Label>
                  <Select value={formData.style} onValueChange={(v) => setFormData({ ...formData, style: v })}>
                    <SelectTrigger className="input-oracle">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {styleOptions.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* What Delphi will create */}
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                <p className="font-medium text-primary mb-2">Delphi will automatically:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>✓ Interpret your prompt intelligently</li>
                  <li>✓ Fill in any missing details</li>
                  <li>✓ Generate high-quality output</li>
                  {selectedTool.id === 'short_video' && <li>✓ Add background music & voiceover</li>}
                  {selectedTool.id === 'influencer' && <li>✓ Create name, personality & avatar</li>}
                </ul>
              </div>

              <Button
                onClick={handleCreate}
                disabled={creating || !formData.prompt.trim() || selectedTool.comingSoon}
                className="w-full btn-oracle h-12"
                data-testid="confirm-create-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Delphi is creating...
                  </>
                ) : selectedTool.comingSoon ? (
                  'Coming Soon'
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Create with Delphi
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default CreateStudioPage;
